import * as React from 'react';

import { SchoolAdminInterface, } from './interfaces'
import AdminRequestModal from './admin_request_modal'

import { requestPost, } from '../../../../../modules/request'
import { PostNavigationBanner, Snackbar, ButtonSpinner, defaultSnackbarTimeout, SMALL_ICON_BASE_SRC, TEACHER, APPROVED, DENIED, PENDING, HIGHER_ED_OR_INTERNATIONAL_SCHOOL } from '../../../../Shared/index'
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor'

const checkCircleImg = <img alt="Checked checkbox" src={`${SMALL_ICON_BASE_SRC}/check-circle.svg`} />
const checkCircleUncheckedImg = <img alt="Unchecked checkbox" src={`${SMALL_ICON_BASE_SRC}/check-circle-unchecked.svg`} />
const MAIL_TO_HELLO_EMAIL = 'mailto:hello@quill.org'
const MAIL_TO_SUPPORT_EMAIL = 'mailto:support@quill.org'

interface VerificationBannerProps {
  needsEmailVerification: boolean;
  adminVerificationStatus: string;
  teacherVerificationStatus: string;
  role: string;
  schoolAdmins: SchoolAdminInterface[];
  alternativeSchoolNames: string[];
  school?: { name: string, };
}

const VerificationBanner = ({
  school,
  needsEmailVerification,
  adminVerificationStatus,
  teacherVerificationStatus,
  role,
  schoolAdmins,
  alternativeSchoolNames,
}: VerificationBannerProps) => {
  const [showAdminRequestModal, setShowAdminRequestModal] = React.useState<boolean>(false)
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>('');
  const [submittedVerificationRequest, setSubmittedVerificationRequest] = React.useState<boolean>(false)
  const [submittedEmailVerificationRequest, setSubmittedEmailVerificationRequest] = React.useState<boolean>(false)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  function openAdminRequestModal() { setShowAdminRequestModal(true) }
  function closeAdminRequestModal() { setShowAdminRequestModal(false) }

  function onAdminRequestSuccess(snackbarCopy) {
    closeAdminRequestModal()
    setSubmittedVerificationRequest(true)
    setSnackbarText(snackbarCopy)
    setShowSnackbar(true)
  }

  function verifyEmail() {
    setSubmittedEmailVerificationRequest(true)

    requestPost(`${window.postbuildEnv.DEFAULT_URL}/verify_emails/require_email_verification`, { email_redirect_url: window.location.href }, (body) => {
      if (body.redirect) {
        window.location.href = body.redirect
      } else {
        window.location.reload()
      }
    })
  }

  const renderRightSide = () => {
    const schoolCheckbox = !school ? checkCircleUncheckedImg : checkCircleImg
    const emailVerificationCheckbox = needsEmailVerification ? checkCircleUncheckedImg : checkCircleImg
    const teacherVerificationCheckbox = teacherVerificationStatus !== APPROVED ? checkCircleUncheckedImg : checkCircleImg

    if (school && alternativeSchoolNames.includes(school.name)) {
      return null
    }
    return (
      <div className="checklist">
        <div className="checklist-item">{schoolCheckbox}Select your school</div>
        <div className="checklist-item">{emailVerificationCheckbox}Verify your email</div>
        <div className="checklist-item">{teacherVerificationCheckbox}Get verified by an admin</div>
      </div>
    )
  }

  const renderTeacherBanner = () => {
    let heading
    let copy
    let button

    if (school && HIGHER_ED_OR_INTERNATIONAL_SCHOOL.includes(school.name)) {
      heading = "Action required"
      copy = "Please email us at support@quill.org or contact us through the chat widget to see if this feature can be enabled for your account."
      button = {
        href: MAIL_TO_SUPPORT_EMAIL,
        text: 'Email us',
        target: '_blank'
      }
    } else if (!school || alternativeSchoolNames.includes(school.name)) {
      heading = 'Action required'
      copy = <React.Fragment>Please select your school to fully unlock this feature. If your school is not listed please <a href={MAIL_TO_HELLO_EMAIL} rel="noopener noreferrer" target="_blank">contact us</a>.</React.Fragment>
      button = {
        href: '/teachers/my_account',
        text: 'Select school'
      }
    } else if (needsEmailVerification) {
      heading = 'Action required'
      copy = 'Please verify your email to fully unlock this feature'
      button = {
        onClick: submittedEmailVerificationRequest ? () => {} : verifyEmail,
        text: submittedEmailVerificationRequest ? <ButtonSpinner variant="light" /> : 'Verify your email'
      }
    } else if (schoolAdmins.length === 0) {
      heading = 'Action required'
      copy = `To get verified and fully unlock this feature, an admin needs to verify you. ${school.name} does not have an admin. It's free and takes just a minute to invite one.`
      button = {
        href: '/admin_access',
        text: 'Invite an admin'
      }
    } else if (teacherVerificationStatus === PENDING || submittedVerificationRequest) {
      heading = 'Your request has been sent'
      copy = 'Your verification request is pending review. Once verified, you will receive an email letting you know that you have full access to this feature. If you need help in the meantime, contact an admin.'
      button = {
        href: '/admin_access',
        text: 'Contact an admin'
      }
    } else if (teacherVerificationStatus === DENIED) {
      heading = "Your admin couldn't verify you"
      copy = `Sorry, your admin couldn't verify you as a teacher at ${school.name}. If you need help, contact us.`
      button = {
        href: MAIL_TO_HELLO_EMAIL,
        text: 'Contact us',
        target: '_blank'
      }
    } else if (!teacherVerificationStatus) {
      heading = 'Action required'
      copy = `You need to be verified by an admin as a teacher at ${school.name} to fully unlock this feature`
      button = {
        onClick: openAdminRequestModal,
        text: 'Request verification'
      }
    }

    return (
      <PostNavigationBanner
        bannerColor="grey"
        bannerStyle="light-mode"
        bodyText={copy}
        buttons={[button]}
        primaryHeaderText={heading}
        rightSide={renderRightSide()}
      />
    )
  }

  const renderAdminBanner = () => {
    let heading = 'Action required'
    let copy = 'Please complete the verification process to fully unlock this feature.'
    let button = {
      href: '/teachers/premium_hub',
      text: 'Go to Premium Hub'
    }

    switch(adminVerificationStatus) {
      case PENDING:
        heading = "We're reviewing your request"
        copy = 'Your verification request is pending approval. Once approved, you will be able to fully unlock this feature. If you need help in the meantime, contact us.'
        button = {
          href: MAIL_TO_HELLO_EMAIL,
          text: 'Contact us',
          target: '_blank'
        }
        break
      case DENIED:
        heading = "We couldn't verify you"
        copy = `Sorry, we couldn't verify you as an admin of ${school.name}. This means you cannot access this feature. If you need help, contact us.`
        button = {
          href: MAIL_TO_HELLO_EMAIL,
          text: 'Contact us',
          target: '_blank'
        }
        break
    }

    return (
      <PostNavigationBanner
        bannerColor="grey"
        bannerStyle="light-mode"
        bodyText={copy}
        buttons={[button]}
        primaryHeaderText={heading}
      />
    )
  }

  return (
    <div className="verification-banner-container">
      {showAdminRequestModal && (
        <AdminRequestModal
          closeModal={closeAdminRequestModal}
          onSuccess={onAdminRequestSuccess}
          schoolAdmins={schoolAdmins}
          schoolName={school.name}
        />
      )}
      <Snackbar text={snackbarText} visible={showSnackbar} />
      {role === TEACHER ? renderTeacherBanner() : renderAdminBanner()}
    </div>
  )
}

export default VerificationBanner
